import { type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid";
import { Box, Button as MuiButton, Checkbox, Tooltip } from "@mui/material";
import {
  convertFullWidthDigitsAndHyphenToHalfWidth,
  convertTypeEnglishToJapanese,
  formatArea,
} from "../../utils";
import { TagGrid } from "../TagGrid";
import { format, parseISO } from "date-fns";
import type { AdditionalFieldConfig } from "@/features/accountSettings/types/additionalFields";

export const createGridColumns = (
  handleDelete: (id: number) => void,
  availableFields?: AdditionalFieldConfig | null
): GridColDef[] => {
  const baseColumns: GridColDef[] = [
    {
      field: "bookType",
      headerName: "種別",
      width: 70,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }: GridRenderCellParams) => {
        return (
          <Box sx={{ py: 1 }}>
            {convertTypeEnglishToJapanese(String(value))}
          </Box>
        );
      },
    },
    {
      field: "prefName",
      headerName: "都道府県",
      width: 80,
      sortable: true,
      align: "left",
      headerAlign: "center",
      renderCell: ({ value }: GridRenderCellParams) => {
        return <Box sx={{ py: 1 }}>{value}</Box>;
      },
    },
    {
      field: "location",
      headerName: "所在",
      width: 240,
      sortable: true,
      align: "left",
      headerAlign: "center",
      renderCell: ({ value }: GridRenderCellParams) => {
        return (
          <Box sx={{ whiteSpace: "normal", py: 1, lineHeight: 1.4 }}>
            {value}
          </Box>
        );
      },
    },
    {
      field: "chibanKaokuNumber",
      headerName: "地番または家屋番号",
      width: 140,
      sortable: true,
      align: "left",
      headerAlign: "center",
      renderCell: ({ value }: GridRenderCellParams) => {
        return (
          <Box sx={{ py: 1 }}>
            {convertFullWidthDigitsAndHyphenToHalfWidth(value as string)}
          </Box>
        );
      },
      renderHeader: () => (
        <strong>
          地番または
          <br />
          家屋番号
        </strong>
      ),
    },
    {
      field: "area",
      headerName: "推計地積(㎡)",
      width: 100,
      sortable: true,
      align: "left",
      headerAlign: "center",
      renderCell: ({ value }: GridRenderCellParams) => {
        return <Box sx={{ py: 1 }}>{formatArea(value as number)}</Box>;
      },
      renderHeader: () => (
        <strong>
          推計地積
          <br />
          (㎡)
        </strong>
      ),
    },
    {
      field: "tags",
      headerName: "タグ",
      width: 200,
      sortable: true,
      align: "left",
      headerAlign: "center",
      renderCell: ({ value }: GridRenderCellParams) => {
        const tags = value as string[] | undefined;
        return (
          <Box sx={{ py: 1 }}>
            <TagGrid tags={tags ?? []} />
          </Box>
        );
      },
      renderHeader: () => <strong>タグ</strong>,
    },
    {
      field: "remarks",
      headerName: "備考",
      width: 200,
      sortable: true,
      align: "left",
      headerAlign: "center",
      renderCell: ({ value }: GridRenderCellParams) => {
        const text = value as string | undefined;
        const displayText =
          text && text.length > 20 ? `${text.slice(0, 20)}...` : text ?? "";
        return (
          <Box sx={{ py: 1 }}>
            {text && text.length > 20 ? (
              <Tooltip title={text} placement="top">
                <span>{displayText}</span>
              </Tooltip>
            ) : (
              <span>{displayText}</span>
            )}
          </Box>
        );
      },
      renderHeader: () => <strong>備考</strong>,
    },
  ];

  // 追加項目の各フィールドを個別の列として生成
  const additionalFieldsColumns: GridColDef[] = availableFields?.fields
    ? availableFields.fields.map((field) => {
        const columnWidth = field.fieldType === "datetime" ? 220 : 180;

        return {
          field: `additionalField_${field.id}`,
          headerName: field.key,
          width: columnWidth,
          sortable: false,
          align: "left",
          headerAlign: "center",
          renderCell: (params: GridRenderCellParams) => {
            // パフォーマンス最適化: 事前にパース済みのadditionalFieldsを使用
            const row = params.row as {
              _parsedAdditionalFields?: Record<string, string>;
            };
            const parsed = row._parsedAdditionalFields ?? {};
            const value = parsed[field.id] ?? "";

            // 真偽値フィールドの場合は読み取り専用チェックボックスを表示（空の場合もunchecked表示）
            if (field.fieldType === "boolean") {
              return (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    py: 1,
                  }}
                >
                  <Checkbox checked={value === "true"} disabled={true} />
                </Box>
              );
            }

            // 日付・日時フィールドの表示用フォーマット
            let displayValue = value;
            if (value && typeof value === "string") {
              try {
                if (field.fieldType === "date") {
                  // 日付フィールド: YYYY-MM-DD → YYYY/MM/DD
                  const parsed = parseISO(value);
                  if (!isNaN(parsed.getTime())) {
                    displayValue = format(parsed, "yyyy/MM/dd");
                  }
                } else if (field.fieldType === "datetime") {
                  // 日時フィールド: YYYY-MM-DDTHH:mm → YYYY/MM/DD HH:mm
                  const parsed = parseISO(value);
                  if (!isNaN(parsed.getTime())) {
                    displayValue = format(parsed, "yyyy/MM/dd HH:mm");
                  }
                }
              } catch {
                // パースエラーの場合は元の値を使用
                displayValue = value;
              }
            }

            return (
              <Box sx={{ py: 1, whiteSpace: "normal", lineHeight: 1.4 }}>
                {displayValue}
              </Box>
            );
          },
          renderHeader: () => <strong>{field.key}</strong>,
        };
      })
    : [];

  const actionColumn: GridColDef = {
    field: "action",
    headerName: "Action",
    minWidth: 80,
    sortable: false,
    align: "center",
    headerAlign: "center",
    renderCell: (params) => {
      return (
        <Box sx={{ py: 1 }}>
          <MuiButton
            size="small"
            variant="outlined"
            color="error"
            onClick={() => {
              handleDelete(params.id as number);
            }}
          >
            削除する
          </MuiButton>
        </Box>
      );
    },
  };

  return [...baseColumns, ...additionalFieldsColumns, actionColumn];
};
